import { useMemo, type ReactNode } from 'react';
import { useLingui } from '@lingui/react/macro';
import { CharacterCountLabelProvider, type CharacterCountLabel } from '@worldcoo/ui/components/form/character-count';
import { FormOptionalLabelProvider } from '@worldcoo/ui/components/form/form-optional-label';
import { SimpleTextEditorTextsProvider } from '@worldcoo/ui/components/form/text-editor/simple-text-editor-texts';

export function FormTranslationsProvider({ children }: { children: ReactNode }) {
	const { t, i18n } = useLingui();
	const editorTexts = useMemo(
		() => ({
			bold: t`Bold`,
			italic: t`Italic`,
			underline: t`Underline`,
			bulletList: t`Bullet list`,
			numberedList: t`Numbered list`,
			link: t`Link`,
			insertLink: t`Insert link`,
			openLinkInNewTab: t`Open link in a new tab`,
			clearLink: t`Clear link`,
			copy: t`Copy`,
			copied: t`Copied!`,
			url: t`URL`,
			urlPlaceholder: t`Enter URL`,
			displayText: t`Display text (optional)`,
			displayTextPlaceholder: t`Enter display text`,
			openInNewTab: t`Open in new tab`,
			save: t`Save`,
		}),
		// oxlint-disable-next-line react-hooks/exhaustive-deps -- t is locale-bound; i18n.locale drives the refresh
		[i18n.locale],
	);
	const characterCountLabel = useMemo(
		(): CharacterCountLabel =>
			({ count, min, max }) =>
				t`${count}/${min}-${max} characters`,
		// oxlint-disable-next-line react-hooks/exhaustive-deps -- t is locale-bound; i18n.locale drives the refresh
		[i18n.locale],
	);

	return (
		<FormOptionalLabelProvider value={t`Optional`}>
			<CharacterCountLabelProvider value={characterCountLabel}>
				<SimpleTextEditorTextsProvider value={editorTexts}>{children}</SimpleTextEditorTextsProvider>
			</CharacterCountLabelProvider>
		</FormOptionalLabelProvider>
	);
}
